import type { CatalogResult } from "./model-catalog.js";
import { modelQuotaVerdict, type ModelQuota } from "./quota.js";

/** One rung of the preference ladder. Deployment params live WITH the entry so
 *  the pick is deterministic given (list, catalog, quota). */
export interface Rung {
  model: string;
  /** `az … deployment create --model-format`. "OpenAI" | "xAI" | … */
  format: string;
  /** `--model-version`. A DEPLOY parameter, never a filter key — see planCascade. */
  version: string;
  /** `--sku-capacity`, sized for ONE founder in ONE conversation. */
  capacity: number;
}

/**
 * The ordered preference ladder: a WHITELIST WITH AN ORDER. The live catalog is
 * a FILTER, never a source — a rung MS removes or de-agents simply falls out,
 * but nothing new reaches founders without a deliberate edit here. That edit is
 * the vetting moment for a model family's suitability.
 *
 * Ratified 2026-07-23, grounded in the live Foundry catalog:
 *  - The three gpt-5.6 variants are documented by Microsoft as capability-
 *    identical with NO published ranking, so their relative order is stable but
 *    ARBITRARY (alphabetical). Any of the three is an equivalent outcome. They
 *    are listed separately because quota is granted PER MODEL, so each is a
 *    genuine second chance.
 *  - gpt-5.5 is deliberately absent: Microsoft's quota tiers give it 0 TPM
 *    GlobalStandard in Tiers 1-4, and it would sit only between gpt-5.6-terra
 *    and gpt-5.4 — reachable solely by a subscription with 5.5 quota but no 5.6
 *    quota, which is close to an empty set.
 *  - gpt-chat-latest is deliberately absent: it is a moving pointer with several
 *    concurrent live versions, which breaks determinism.
 *  - grok-4.3 is the OpenAI-exhausted fallback. It is agent-eligible in 17 of the
 *    18 hosted-agent regions (absent in southeastasia).
 */
export const WHITELIST: readonly Rung[] = [
  { model: "gpt-5.6-luna", format: "OpenAI", version: "2026-07-09", capacity: 100 },
  { model: "gpt-5.6-sol", format: "OpenAI", version: "2026-07-09", capacity: 100 },
  { model: "gpt-5.6-terra", format: "OpenAI", version: "2026-07-09", capacity: 100 },
  { model: "gpt-5.4", format: "OpenAI", version: "2026-03-05", capacity: 100 },
  { model: "grok-4.3", format: "xAI", version: "1", capacity: 100 },
  { model: "gpt-4.1-mini", format: "OpenAI", version: "2025-04-14", capacity: 50 },
] as const;

export type RungOutcome =
  | "deployed"
  | "not-reached"
  | "not-in-catalog"
  | "not-agent-eligible"
  | "no-global-standard"
  | "no-quota"
  | "deploy-rejected-quota"
  | "deploy-rejected-region"
  | "deploy-unverified"
  | "catalog-unverified"
  | "aborted-deadline";

export interface TraceRow {
  model: string;
  outcome: RungOutcome;
  /** Raw diagnostic (ARM text). NEVER shown to the founder — trace/debug only. */
  detail?: string;
}

export interface CascadePlan {
  candidates: Rung[];
  skipped: TraceRow[];
}

const GLOBAL_STANDARD = "GlobalStandard";

/**
 * Filter the whitelist against the region's live catalog and quota.
 *
 * Exclusion requires POSITIVE EVIDENCE OF IMPOSSIBILITY. Only an EXPLICIT zero
 * quota excludes a rung; `unknown` and no-usage-row models stay candidates and
 * the walk decides. This is not stylistic: gpt-4.1-mini has no GlobalStandard
 * usage row in eastus2 yet deploys on every install, so a positive-quota
 * requirement would reject the ladder's own floor. See the named regression test.
 *
 * The catalog is matched BY NAME ONLY. `version` is a deploy parameter, so a
 * stale pin surfaces loudly as a deploy-stage rejection in the trace rather than
 * quietly as a filter skip.
 *
 * A failed catalog probe yields NO candidates and marks every rung
 * `catalog-unverified` — we cannot claim a rung is absent when we could not read
 * the catalog.
 */
export function planCascade(
  whitelist: readonly Rung[],
  catalog: CatalogResult,
  quota: ModelQuota[],
): CascadePlan {
  if (!catalog.ok) {
    return {
      candidates: [],
      skipped: whitelist.map((r) => ({ model: r.model, outcome: "catalog-unverified" as const, detail: catalog.error })),
    };
  }

  const rowsByName = new Map<string, typeof catalog.rows>();
  for (const row of catalog.rows) {
    const list = rowsByName.get(row.name);
    if (list) list.push(row); else rowsByName.set(row.name, [row]);
  }

  const candidates: Rung[] = [];
  const skipped: TraceRow[] = [];

  for (const rung of whitelist) {
    const rows = rowsByName.get(rung.model);
    if (!rows || rows.length === 0) { skipped.push({ model: rung.model, outcome: "not-in-catalog" }); continue; }
    // A catalog lists one row per model VERSION, and versions differ in what they
    // support. Eligibility needs both properties on the SAME row, so scan all of
    // them rather than trusting whichever the API happened to return first.
    const agentRows = rows.filter((r) => r.agentsV2);
    if (agentRows.length === 0) { skipped.push({ model: rung.model, outcome: "not-agent-eligible" }); continue; }
    if (!agentRows.some((r) => r.skus.includes(GLOBAL_STANDARD))) {
      skipped.push({ model: rung.model, outcome: "no-global-standard" }); continue;
    }
    if (modelQuotaVerdict(quota, rung.model).verdict === "no_quota") {
      skipped.push({ model: rung.model, outcome: "no-quota" });
      continue;
    }
    candidates.push(rung);
  }

  return { candidates, skipped };
}

export interface CascadeResult {
  /** The rung whose deployment succeeded, or null → caller falls back to the
   *  persona-frontmatter floor. Never throws; the cascade is non-fatal. */
  chosen: Rung | null;
  /** One row per whitelist rung, in whitelist order. This IS the observability. */
  trace: TraceRow[];
}

export type DeployFn = (rung: Rung) => Promise<void>;

export interface WalkOpts {
  /** Injected clock — keeps the walk pure and the deadline test deterministic. */
  now: () => number;
  deadlineAt: number;
  /** Per-rung callback for curated founder-facing narration. */
  onRung?: (model: string, outcome: RungOutcome) => void;
}

// Deliberately proximity-based rather than a verb list: "quota exceeded",
// "quota was exceeded", "quota has been exceeded" and the capacity phrasings all
// need to match, and enumerating linking verbs guarantees the next phrasing is
// missed. Requires the two tokens within one sentence ([^.]) so an unrelated
// mention of quota elsewhere in a longer message cannot trigger it.
//
// This outcome is the only one that leads to telling a user their subscription
// lacks quota, so an ambiguous message must fall through to `deploy-unverified`:
// over-matching produces confident bad advice, under-matching only costs a
// suggestion we could have made.
const QUOTA_RE = /insufficient\s*quota|not enough quota|quota limit|quota\b[^.]{0,40}exceed|exceed\w*\b[^.]{0,40}quota|capacity[^.]{0,40}exceed|exceed\w*\b[^.]{0,40}capacity/i;
const REGION_RE = /not available in|not supported in|NotAvailableInRegion|InvalidResourceLocation/i;
const CONFLICT_RE = /\bconflict\b|\b409\b/i;

/**
 * Map an ARM failure to a rung outcome. Deliberately conservative: anything not
 * confidently attributable becomes `deploy-unverified`, which SUPPRESSES the
 * downstream quota pitch. We never advise a founder to request quota on the
 * strength of a reason we did not actually observe.
 */
export function classifyDeployError(message: string): RungOutcome {
  if (QUOTA_RE.test(message)) return "deploy-rejected-quota";
  if (REGION_RE.test(message)) return "deploy-rejected-region";
  return "deploy-unverified";
}

/**
 * Walk the candidates top-down; the DEPLOY ATTEMPT is the oracle. First success
 * wins. The attempt cap equals the whitelist length, so no rung is structurally
 * unreachable.
 *
 * The deadline is a LIVENESS BACKSTOP, never a selector: if it fires the walk
 * aborts and records `aborted-deadline`, so a timeout can never be silently
 * misread as "no rung was available". Given attempts that fail on ARM validation
 * in seconds, it should never fire in practice.
 *
 * A 409 is transient (this walk can race the installer's own deploy-model phase
 * on the same account): retry once, then `deploy-unverified` — never quota.
 */
export async function walkCascade(
  whitelist: readonly Rung[],
  plan: CascadePlan,
  deploy: DeployFn,
  opts: WalkOpts,
): Promise<CascadeResult> {
  const outcomes = new Map<string, TraceRow>();
  for (const s of plan.skipped) outcomes.set(s.model, s);

  let chosen: Rung | null = null;
  let aborted = false;

  for (const rung of plan.candidates) {
    if (chosen) break;
    if (opts.now() >= opts.deadlineAt) { aborted = true; break; }

    let row: TraceRow;
    try {
      await deploy(rung);
      row = { model: rung.model, outcome: "deployed" };
      chosen = rung;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (CONFLICT_RE.test(msg)) {
        // One retry: a 409 here is most likely the installer touching the same
        // account concurrently, not a property of this rung.
        try {
          await deploy(rung);
          row = { model: rung.model, outcome: "deployed" };
          chosen = rung;
        } catch (e2) {
          const msg2 = e2 instanceof Error ? e2.message : String(e2);
          row = {
            model: rung.model,
            outcome: CONFLICT_RE.test(msg2) ? "deploy-unverified" : classifyDeployError(msg2),
            detail: msg2,
          };
        }
      } else {
        row = { model: rung.model, outcome: classifyDeployError(msg), detail: msg };
      }
    }
    outcomes.set(rung.model, row);
    opts.onRung?.(row.model, row.outcome);
  }

  const trace: TraceRow[] = whitelist.map((r) =>
    outcomes.get(r.model) ?? { model: r.model, outcome: aborted ? "aborted-deadline" : "not-reached" },
  );

  return { chosen, trace };
}

/**
 * The one sentence that authorizes the intake persona to offer a quota request.
 *
 * Load-bearing across a file boundary: the persona matches this verbatim. It is a
 * shared constant rather than a literal in both places because a silent mismatch
 * disables the offer permanently with nothing turning red. Changing this text is a
 * behavior change to the persona — see intake-pitch-contract.test.ts.
 */
export const QUOTA_INVITATION = "You can offer to help request that quota.";

export type NoteStatus = "top" | "lesser-quota" | "lesser-unavailable" | "lesser-unverified";

export interface NoteDecision {
  status: NoteStatus;
  /** null when the cascade produced no verified model (the persona falls back to
   *  the frontmatter floor, which this module deliberately does not name). */
  runningModel: string | null;
  /** The model to request quota for. Non-null IFF status === "lesser-quota". */
  pitchModel: string | null;
  /** Rungs ABOVE the pitch target that are certainly unavailable here — mentioned
   *  for honesty, never used as the pitch target. */
  unavailableAbovePitch: string[];
}

/** Outcomes that are CERTAIN and non-quota: we checked, and no quota grant fixes them. */
const CERTAIN_UNAVAILABLE: ReadonlySet<RungOutcome> = new Set([
  "not-in-catalog", "not-agent-eligible", "no-global-standard", "deploy-rejected-region",
]);
/** Outcomes that are CERTAIN and quota-attributable: a quota request is real advice. */
const QUOTA_BLOCKED: ReadonlySet<RungOutcome> = new Set(["no-quota", "deploy-rejected-quota"]);

/**
 * Derive the note's status from the OBSERVED outcomes of the rungs above the
 * chosen one.
 *
 * The pitch targets the highest QUOTA-BLOCKED rung, not the highest blocked
 * rung: region-unavailable rungs neither trigger nor suppress it, because no
 * quota grant makes a model appear in a region that does not offer it. If no
 * better rung is quota-blocked there is no pitch at all.
 *
 * `lesser-unavailable` requires EVERY better rung to be certainly unavailable —
 * the status asserts "I checked, none are offered here". Any uncertain rung among
 * them downgrades to `lesser-unverified`, so the persona never states a reason we
 * did not observe.
 */
export function decideNote(whitelist: readonly Rung[], result: CascadeResult): NoteDecision {
  const chosenIdx = result.chosen ? whitelist.findIndex((r) => r.model === result.chosen?.model) : whitelist.length;
  // `findIndex` returns -1 only if `result.chosen` names a model absent from
  // `whitelist`, which never happens on the real path (the chosen rung always
  // comes FROM the whitelist). The clamp to 0 is a defensive fallback for that
  // unreachable case, and it resolves to "treat as no better rungs" — the safe
  // direction, since it can only ever suppress a pitch, never fabricate one.
  const better = result.trace.slice(0, Math.max(chosenIdx, 0));
  const runningModel = result.chosen?.model ?? null;

  if (chosenIdx === 0) {
    return { status: "top", runningModel, pitchModel: null, unavailableAbovePitch: [] };
  }

  const pitch = better.find((t) => QUOTA_BLOCKED.has(t.outcome));
  if (pitch) {
    const pitchIdx = better.findIndex((t) => t.model === pitch.model);
    return {
      status: "lesser-quota",
      runningModel,
      pitchModel: pitch.model,
      unavailableAbovePitch: better.slice(0, pitchIdx).filter((t) => CERTAIN_UNAVAILABLE.has(t.outcome)).map((t) => t.model),
    };
  }

  const allCertain = better.length > 0 && better.every((t) => CERTAIN_UNAVAILABLE.has(t.outcome));
  return {
    status: allCertain ? "lesser-unavailable" : "lesser-unverified",
    runningModel,
    pitchModel: null,
    unavailableAbovePitch: [],
  };
}

/** Joins names into plain-English list prose: "m1" / "m1 and m2" / "m1, m2 and m3". */
const listNames = (names: string[]): string =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * Render the human-readable preference table for guides/bootstrap.md.
 *
 * Deliberately omits `version`: the doc pins the PREFERENCE ORDER, and a version
 * bump that does not change preference should not churn the runbook. A drift test
 * asserts this output byte-for-byte against the doc's sentinel-delimited block.
 */
export function renderCascadeTable(whitelist: readonly Rung[]): string {
  const header = "| # | Model | Family | Capacity |\n|---|---|---|---|";
  const rows = whitelist.map((r, i) => `| ${String(i + 1)} | \`${r.model}\` | ${r.format} | ${String(r.capacity)} |`);
  return [header, ...rows].join("\n");
}

/**
 * Render the persona-facing note. CONSENT-ADJACENT COPY: the TEMPLATE is what
 * the copy register pins; dynamic values are interpolated. Keep every claim
 * cadenced with what the cascade actually observed.
 *
 * The persona branches on a sentence that is PRESENT OR ABSENT — it must never
 * have to reason about quota itself.
 */
export function renderChosenModelNote(d: NoteDecision): string {
  const subject = d.runningModel
    ? `You are running on ${d.runningModel}.`
    : "You are running on this install's default model.";

  switch (d.status) {
    case "top":
      return `${subject} That is the best available model for this install.`;
    case "lesser-quota": {
      const aside = d.unavailableAbovePitch.length > 0
        ? ` ${listNames(d.unavailableAbovePitch)} ${d.unavailableAbovePitch.length > 1 ? "are" : "is"} not offered for this install.`
        : "";
      return `${subject} A stronger model, ${d.pitchModel ?? ""}, is offered here, but this subscription has no quota for it.${aside} ${QUOTA_INVITATION}`;
    }
    case "lesser-unavailable":
      return `${subject} No stronger model is offered for this install right now.`;
    case "lesser-unverified":
      return `${subject} It was not possible to check which stronger models this subscription can run, so do not make claims about what is or is not available.`;
  }
}
